'use client'

import { Dispatch, SetStateAction, useState } from 'react'
import { usePlatformEvents, UsePlatformEventsPropsBase } from './usePlatformEvents'

interface UseLiveDataPlatformEventsProps<TLiveData> extends UsePlatformEventsPropsBase {
    initialData?: TLiveData
    onEvent?: (setData: Dispatch<SetStateAction<TLiveData | undefined>>, event: MessageEvent) => void | Promise<void>
}

interface UseLiveDataPlatformEventsReturnValue<TLiveData> {
    message: string
    data?: TLiveData
}

/**
 * IMPORTANT!! This specific hook has never been used outside of POC. // TODO: remove this comment when this is no longer accurate.
 *
 * Expands upon `usePlatformEvents` by keeping message/data state.
 *
 * @example
 * ```ts
 * const { data, message } = useLiveDataPlatformEvents({
 *     scope: PlatformEventScopes.Company,
 *     code: companyCode,
 *     initialData: company,
 *     event: PlatformEventEvents.AddressUpdated,
 *     onEvent: async (setData) => {
 *         const response = await readCompanyDetails(companyCode)
 *         setData(response?.data)
 *     },
 * })
 * ```
 *
 * @param { scope } - Scope in which to listen for events.
 * @param { code } - Identifier inside the scope (for example a company code).
 * @param { initialData } - Initial value for `data`.
 * @param { event } - Optional custom event name; defaults to the SSE "message" event.
 * @param { onEvent } - Callback invoked per event, with `setData` and the raw `MessageEvent`.
 *
 * @returns { message } - Latest raw event message string.
 * @returns { data } - Live state that can be updated from `onEvent`.
 */
export const useLiveDataPlatformEvents = <TLiveData>({
    scope,
    code,
    initialData,
    event,
    onEvent,
}: UseLiveDataPlatformEventsProps<TLiveData>): UseLiveDataPlatformEventsReturnValue<TLiveData> => {
    const [message, setMessage] = useState<string>('')
    const [data, setData] = useState<TLiveData | undefined>(initialData)

    usePlatformEvents({
        scope,
        code,
        event,
        onEvent: async (messageEvent) => {
            try {
                await onEvent?.(setData, messageEvent)
            } catch {
                return
            } finally {
                setMessage(messageEvent.data)
            }
        },
    })

    return { message, data }
}
